import { CheckCircleOutlined, EnvironmentOutlined, ToolOutlined } from "@ant-design/icons"
import { Button, Tag } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { resolveGcContact } from "../helpers/recipient.ts"
import { friendlyContactRole } from "../helpers/contact-role.ts"
import { formatPortalDate } from "../helpers/format-date.ts"
import type { PortalProjectListItem } from "../types/portal.types.ts"

export const COMPLETED_PROJECT_STATUS_LABELS = new Set(["Completed", "Cancelled"])

/** A project's list-row payload has no next-milestone field (only its
 * current phase name) — that requires the project's full phase list, which
 * is fetched per-project, not for the whole list. Rather than guess at a
 * "next milestone" this card only shows what the list actually carries;
 * the real next-milestone line lives on the project's own page/Home detail,
 * which do have the full phase schedule. */
export function CustomerProjectCard({
  project,
  pendingCount,
  onOpen
}: {
  project: PortalProjectListItem
  pendingCount: number
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const isCompleted =
    typeof project.project_status === "string" &&
    COMPLETED_PROJECT_STATUS_LABELS.has(project.project_status)
  const contact = resolveGcContact(project.project_managers)

  return (
    <CardComponent className="!p-0 overflow-hidden" styles={{ body: { padding: 0 } }}>
      <div className="flex h-full items-stretch">
        {project.main_photo ? (
          <img
            src={project.main_photo}
            alt={project.lead_name}
            className="w-[120px] shrink-0 object-cover sm:w-[136px]"
          />
        ) : null}

        <div className="flex flex-1 flex-col gap-2.5 p-5">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5">
              {!project.main_photo && (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ToolOutlined />
                </div>
              )}
              <h3 className="text-[17px] font-semibold leading-tight text-gray-900">
                {project.lead_name}
              </h3>
            </div>
            {typeof project.project_status === "string" && (
              <Tag color={isCompleted ? "default" : "blue"}>{project.project_status}</Tag>
            )}
          </div>

          {project.address && (
            <p className="flex items-center gap-1.5 text-sm text-gray-500">
              <EnvironmentOutlined />
              <span className="truncate">
                {[project.address, project.city].filter(Boolean).join(", ")}
              </span>
            </p>
          )}

          <div className="flex flex-col gap-1 text-sm">
            {isCompleted ? (
              <p className="text-gray-600">
                {project.end_date
                  ? t("portal.projects.completedOn", { date: formatPortalDate(project.end_date) })
                  : t("portal.projects.completedLabel")}
              </p>
            ) : (
              <p className="flex gap-1.5">
                <span className="shrink-0 text-gray-400">{t("portal.projects.currentWork")}</span>
                <span className="truncate text-gray-700">
                  {project.phase || t("portal.home.noPhaseInProgress")}
                </span>
              </p>
            )}
            {contact && (
              <p className="flex gap-1.5">
                <span className="shrink-0 text-gray-400">{t("portal.projects.assignedTo")}</span>
                <span className="truncate text-gray-700">
                  {[contact.first_name, contact.last_name].filter(Boolean).join(" ")}
                  {" · "}
                  {friendlyContactRole(contact.role)}
                </span>
              </p>
            )}
          </div>

          <div className="mt-auto flex items-center justify-between gap-2 pt-1">
            {pendingCount > 0 ? (
              <span className="text-xs font-medium text-amber-600">
                {t("portal.projects.pendingCount", { count: pendingCount })}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <CheckCircleOutlined />
                {t("portal.projects.noActionsNeeded")}
              </span>
            )}
            <Button type="primary" onClick={onOpen}>
              {isCompleted ? t("portal.projects.viewProject") : t("portal.projects.openProject")}
            </Button>
          </div>
        </div>
      </div>
    </CardComponent>
  )
}
